import type { MessageTemplateSettingsSectionType } from "@repo/app-types/entities/messageTemplate";
import {
  isInterviewSettingsSectionType,
  type MessageTemplate,
  MessageTemplateType,
  messageTemplateTypesBySettingsSectionType,
  MessageTemplateTypeToLabel,
} from "@repo/app-types/entities/messageTemplate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import {
  renderMultilineContent,
  renderSingleLineContent,
} from "@repo/ui/content/renderers";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { type FC, Fragment, useState } from "react";
import {
  useAllMessageTemplatesQuery,
  useDeleteMessageTemplateMutation,
} from "../../../api/entities/messageTemplate.js";
import { PageLayout } from "../../../components/layout/pageLayout.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSection } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { SettingsTableSectionHeader } from "../shared/settingsTableSectionHeader.js";
import { MessageTemplateCreateDialog } from "./messageTemplateCreateDialog.js";
import { MessageTemplateEditDialog } from "./messageTemplateEditDialog.js";

interface MessageTemplateSettingsSectionProps {
  messageTemplateSectionType: MessageTemplateSettingsSectionType;
}

export const MessageTemplateSettingsSection: FC<
  MessageTemplateSettingsSectionProps
> = ({ messageTemplateSectionType }) => {
  const allMessageTemplateTypesForSection =
    messageTemplateTypesBySettingsSectionType[messageTemplateSectionType];

  const { data: allMessageTemplates, isLoading } =
    useAllMessageTemplatesQuery();

  const filteredMessageTemplates = allMessageTemplates?.filter(({ type }) => {
    return allMessageTemplateTypesForSection.includes(type);
  });

  const [deleteMessageTemplate] = useDeleteMessageTemplateMutation();

  const [templateIdToDelete, setTemplateIdToDelete] = useState<
    string | undefined
  >(undefined);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [messageTemplateToEdit, setMessageTemplateToEdit] = useState<
    MessageTemplate | undefined
  >(undefined);

  const isInterviewSectionType = isInterviewSettingsSectionType(
    messageTemplateSectionType,
  );

  function renderTemplateBody(template: MessageTemplate) {
    // "recording" templates are quite long, line-clamp them.
    if (
      template.type === MessageTemplateType.RecordingSummary ||
      template.type === MessageTemplateType.RecordingEmail
    ) {
      return (
        <span className="line-clamp-6">
          {renderMultilineContent(template.body)}
        </span>
      );
    }

    // Render all other message templates as fragments
    return <>{renderMultilineContent(template.body)}</>;
  }

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Form}
        title={
          isInterviewSectionType
            ? settingsSections.messageTemplates.label
            : "Templates"
        }
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.MESSAGE_TEMPLATES}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                icon={<IconPlus className="size-4" />}
                onClick={() => setIsCreateDialogOpen(true)}
                isDisabled={!allowed}
              >
                {isInterviewSectionType
                  ? "Create message template"
                  : "Create template"}
              </Button>
            )}
          </Can>
        }
      />
      <SettingsSection>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {isInterviewSectionType
              ? "Set up message templates and use them in projects to notify candidates when they are advanced or rejected."
              : "Set up templates for automatically generated recording summaries and emails."}
          </div>
          <SettingsTable>
            <SettingsTableHeaderSection>
              <tr>
                <SettingsTableHeaderCell className="w-[80%]">
                  Template
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell className="w-[20%]">
                  Created
                </SettingsTableHeaderCell>
              </tr>
            </SettingsTableHeaderSection>
            <SettingsTableBodySection>
              {isLoading ? <TableSkeleton /> : null}

              {filteredMessageTemplates?.length === 0 ? (
                <SettingsTableEmptyBodyContent colSpan={2}>
                  {isInterviewSectionType
                    ? "No message templates have been created yet."
                    : "No templates have been created yet."}
                </SettingsTableEmptyBodyContent>
              ) : null}

              {allMessageTemplateTypesForSection.map((type) => {
                const templates = filteredMessageTemplates?.filter(
                  (template) => template.type === type,
                );

                if (!templates || templates.length === 0) {
                  return null;
                }

                return (
                  <Fragment key={type}>
                    <SettingsTableSectionHeader>
                      {MessageTemplateTypeToLabel[type]}
                    </SettingsTableSectionHeader>
                    {templates.map((template, index) => (
                      <tr
                        key={`${template.type}-${index}`}
                        className="group relative"
                      >
                        <SettingsTableDataCell>
                          <p className="truncate font-semibold">
                            {renderSingleLineContent(template.subject)}
                          </p>
                          <div className="line-clamp-2">
                            {renderTemplateBody(template)}
                          </div>
                        </SettingsTableDataCell>
                        <SettingsTableDataCell>
                          <SmallTimeStamp
                            timestamp={template.created_at}
                            className="text-sm text-muted-foreground"
                          />

                          <div className="absolute top-1/2 right-3 hidden -translate-y-1/2 cursor-pointer items-center space-x-2 text-muted-foreground group-hover:flex">
                            <Can
                              I={PermissionActions.UPDATE}
                              a={PermissionSubjects.MESSAGE_TEMPLATES}
                            >
                              <IconButton
                                variant={ButtonVariant.Secondary}
                                icon={<IconPencil className="h-4 w-4" />}
                                onClick={() => {
                                  setMessageTemplateToEdit(template);
                                }}
                              />
                            </Can>
                            <Can
                              I={PermissionActions.DELETE}
                              a={PermissionSubjects.MESSAGE_TEMPLATES}
                            >
                              <IconButton
                                variant={ButtonVariant.Secondary}
                                icon={<IconTrash className="h-4 w-4" />}
                                onClick={() => {
                                  setTemplateIdToDelete(template.id);
                                }}
                              />
                            </Can>
                          </div>
                        </SettingsTableDataCell>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </SettingsTableBodySection>
          </SettingsTable>
        </div>
      </SettingsSection>

      <SimpleModal
        isOpen={!!templateIdToDelete}
        onCancel={() => {
          setTemplateIdToDelete(undefined);
        }}
        variant={SimpleModalVariant.Warning}
        title="Delete template"
        subtitle={
          "Are you sure you want to delete this template? This action can't be undone."
        }
        confirmButtonText="Delete template"
        onConfirm={() => {
          if (templateIdToDelete) {
            deleteMessageTemplate(templateIdToDelete);
          }

          setTemplateIdToDelete(undefined);
        }}
      />

      <MessageTemplateCreateDialog
        messageTemplateSectionType={messageTemplateSectionType}
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />

      <MessageTemplateEditDialog
        messageTemplateSectionType={messageTemplateSectionType}
        messageTemplate={messageTemplateToEdit}
        onClose={() => setMessageTemplateToEdit(undefined)}
      />
    </PageLayout>
  );
};
